import { useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Copy, Check, RefreshCw, KeyRound, AlertTriangle } from 'lucide-react';
import { SiShopify, SiWordpress, SiWix, SiSquarespace, SiWebflow, SiGodaddy, SiHtml5 } from 'react-icons/si';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

interface InstallationWizardProps {
  site: { domain: string };
  embedSnippet: string;
  onCopy: (text: string) => void;
  onVerify: () => void;
  verifyPending: boolean;
  onRotate: () => void;
  rotatePending: boolean;
}

const PLATFORMS = [
  { id: 'shopify', name: 'Shopify', icon: SiShopify },
  { id: 'wordpress', name: 'WordPress', icon: SiWordpress },
  { id: 'wix', name: 'Wix', icon: SiWix },
  { id: 'squarespace', name: 'Squarespace', icon: SiSquarespace },
  { id: 'webflow', name: 'Webflow', icon: SiWebflow },
  { id: 'godaddy', name: 'GoDaddy', icon: SiGodaddy },
  { id: 'html', name: 'Other / Not sure', icon: SiHtml5 },
];

const PLATFORM_GUIDES: Record<string, { name: string; steps: ReactNode[]; note?: ReactNode }> = {
  shopify: {
    name: 'Shopify',
    steps: [
      <>Log in to your Shopify admin and click <strong>Online Store</strong>, then <strong>Themes</strong>.</>,
      <>Next to your current theme, click the three dots (<strong>...</strong>) and select <strong>Edit code</strong>.</>,
      <>In the left sidebar under Layout, click <strong>theme.liquid</strong>.</>,
      <>Scroll all the way to the bottom of the file until you see the closing <code>&lt;/body&gt;</code> tag.</>,
      <>Paste your ADA Widget code on a new line just above <code>&lt;/body&gt;</code>.</>,
      <>Click <strong>Save</strong> in the top right.</>
    ],
    note: <>If you use a headless storefront or a page builder app (like PageFly), add the code in that tool's custom code section instead.</>
  },
  wordpress: {
    name: 'WordPress',
    steps: [
      <>Log in to your WordPress admin dashboard.</>,
      <>Go to <strong>Plugins</strong> and click <strong>Add New</strong>.</>,
      <>Search for and install the free plugin called <strong>WPCode</strong> (or any header/footer plugin). Activate it.</>,
      <>Go to the new <strong>Code Snippets</strong> menu and click <strong>Header & Footer</strong>.</>,
      <>Paste your ADA Widget code into the <strong>Footer</strong> box.</>,
      <>Click <strong>Save Changes</strong>.</>
    ],
    note: <><strong>Note:</strong> WordPress.com sites require the Business plan or higher to add custom code plugins.</>
  },
  wix: {
    name: 'Wix',
    steps: [
      <>Go to your Wix dashboard and click <strong>Settings</strong>.</>,
      <>Scroll down to the Advanced section and click <strong>Custom Code</strong>.</>,
      <>Click <strong>+ Add Custom Code</strong>.</>,
      <>Paste your ADA Widget code into the code box. Name it something memorable like "ADA Widget".</>,
      <>Choose <strong>All pages</strong>, set the code to <strong>load once</strong>, and select <strong>Body - end</strong> for the placement.</>,
      <>Click <strong>Apply</strong> and then <strong>Publish</strong> your site.</>
    ],
    note: <><strong>Note:</strong> Adding custom code requires a Wix Premium plan and a connected domain.</>
  },
  squarespace: {
    name: 'Squarespace',
    steps: [
      <>Go to your Squarespace home menu and click <strong>Settings</strong>.</>,
      <>Click <strong>Advanced</strong>, then <strong>Code Injection</strong>.</>,
      <>Scroll down to the <strong>Footer</strong> section.</>,
      <>Paste your ADA Widget code into the text box.</>,
      <>Click <strong>Save</strong> at the top of the panel.</>
    ],
    note: <><strong>Note:</strong> Code Injection requires the Squarespace Business plan or higher.</>
  },
  webflow: {
    name: 'Webflow',
    steps: [
      <>Open your project dashboard and go to <strong>Site Settings</strong> (the gear icon).</>,
      <>Click on the <strong>Custom Code</strong> tab.</>,
      <>Scroll down to the <strong>Footer Code</strong> box (before <code>&lt;/body&gt;</code>).</>,
      <>Paste your ADA Widget code here.</>,
      <>Click <strong>Save Changes</strong> and then <strong>Publish</strong> your site.</>
    ],
    note: <><strong>Note:</strong> Custom code requires a paid Webflow site plan. The widget will not show in the Designer preview, only on your live published site.</>
  },
  html: {
    name: 'your website',
    steps: [
      <>Open the HTML file or layout template that controls your website's pages.</>,
      <>Scroll to the bottom and locate the closing <code>&lt;/body&gt;</code> tag.</>,
      <>Paste your ADA Widget code directly above <code>&lt;/body&gt;</code>.</>,
      <>Save your file and publish your changes to your web server.</>
    ],
    note: <>Not sure what your website uses? Ask the person who built or manages it to add this code to every page, just before the closing &lt;/body&gt; tag. They can also look for a "Custom Code", "Code Injection", or "Tracking Scripts" setting.</>
  }
};

export function InstallationWizard({
  site,
  embedSnippet,
  onCopy,
  onVerify,
  verifyPending,
  onRotate,
  rotatePending
}: InstallationWizardProps) {
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    onCopy(embedSnippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderPlatformInstructions = (id: string) => {
    if (id === 'godaddy') {
      return (
        <div className="space-y-4">
          <div className="flex gap-3 text-amber-600 bg-amber-500/10 p-4 rounded-lg border border-amber-500/20">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <div className="text-sm leading-relaxed">
              <p className="font-semibold mb-1">Basic GoDaddy sites are not supported</p>
              <p>GoDaddy's standard Website Builder (Websites + Marketing) does not allow outside code to run across your whole site. Because of this restriction, the ADA Widget cannot be installed there.</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            <strong>Using WordPress on GoDaddy?</strong> If you are on GoDaddy's Managed WordPress hosting, select WordPress above. That setup is supported.
          </p>
        </div>
      );
    }

    const guide = PLATFORM_GUIDES[id];
    if (!guide) return null;

    return (
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
        <div>
          <h3 className="text-base font-semibold text-foreground mb-3">First, copy your unique code</h3>
          <div className="relative group">
            <pre className="p-4 pr-24 rounded-lg bg-slate-950 text-slate-50 text-sm overflow-x-auto whitespace-pre-wrap break-all shadow-inner border border-slate-800">
              {embedSnippet}
            </pre>
            <Button
              size="sm"
              className="absolute top-3 right-3 bg-white/10 hover:bg-white/20 text-white border-0 transition-colors"
              onClick={handleCopy}
              data-testid={`button-copy-snippet-${id}`}
            >
              {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>

        <div className="border-t border-border pt-8">
          <h3 className="text-base font-semibold text-foreground mb-4">Next, paste it into {guide.name}</h3>
          <ol className="space-y-4">
            {guide.steps.map((step, i) => (
              <li key={i} className="flex gap-4">
                <div className="shrink-0 w-6 h-6 rounded bg-muted text-muted-foreground flex items-center justify-center text-xs font-semibold mt-0.5">
                  {i + 1}
                </div>
                <div className="text-sm text-foreground leading-relaxed pt-0.5">{step}</div>
              </li>
            ))}
          </ol>
          
          {guide.note && (
            <div className="mt-6 p-4 rounded-lg bg-muted/50 border border-border text-sm text-muted-foreground">
              {guide.note}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-12">
      {/* Step 1 */}
      <section>
        <div className="flex items-center gap-4 mb-4">
          <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg shadow-sm">1</div>
          <h2 className="text-xl font-semibold text-foreground">Choose your website platform</h2>
        </div>
        <div className="ml-0 sm:ml-12 grid grid-cols-2 md:grid-cols-4 gap-3">
          {PLATFORMS.map(platform => {
            const Icon = platform.icon;
            const isSelected = selectedPlatform === platform.id;
            return (
              <button
                key={platform.id}
                type="button"
                onClick={() => setSelectedPlatform(platform.id)}
                aria-pressed={isSelected}
                className={`flex flex-col items-center gap-3 p-4 rounded-xl border transition-all duration-200 ${
                  isSelected
                    ? 'border-primary bg-primary/5 shadow-[0_0_0_1px_hsl(var(--primary))]'
                    : 'border-card-border bg-card hover:border-primary/40 hover:bg-muted/50'
                }`}
                data-testid={`platform-select-${platform.id}`}
              >
                <Icon className={`w-8 h-8 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                <span className={`font-medium text-sm ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                  {platform.name}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Step 2 */}
      <section className={`transition-all duration-300 ${selectedPlatform ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
        <div className="flex items-center gap-4 mb-4">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-lg shadow-sm ${
            selectedPlatform ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
          }`}>2</div>
          <h2 className="text-xl font-semibold text-foreground">Add the widget to your site</h2>
        </div>
        
        <div className="ml-0 sm:ml-12">
          {selectedPlatform ? (
            <div className="bg-card border border-card-border rounded-xl p-6 md:p-8 shadow-sm">
              {renderPlatformInstructions(selectedPlatform)}
            </div>
          ) : (
            <div className="bg-muted/20 border border-border border-dashed rounded-xl p-8 text-center text-muted-foreground">
              Select a platform above to see step-by-step instructions.
            </div>
          )}
        </div>
      </section>

      {/* Step 3 */}
      <section className={`transition-all duration-300 ${selectedPlatform ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
        <div className="flex items-center gap-4 mb-4">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-lg shadow-sm ${
            selectedPlatform ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
          }`}>3</div>
          <h2 className="text-xl font-semibold text-foreground">Check your installation</h2>
        </div>
        
        <div className="ml-0 sm:ml-12 bg-card border border-card-border rounded-xl p-6 md:p-8 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div>
            <p className="text-foreground font-medium mb-1 text-lg">Publish your site, then verify</p>
            <p className="text-sm text-muted-foreground max-w-lg leading-relaxed">
              Once you have saved and published your changes, we can check if the widget is actively reporting from <strong>{site.domain}</strong>.
            </p>
          </div>
          <Button 
            size="lg" 
            onClick={onVerify} 
            disabled={verifyPending}
            className="shrink-0 font-semibold"
            data-testid="button-verify-install"
          >
            <RefreshCw className={`w-5 h-5 mr-2 ${verifyPending ? 'animate-spin' : ''}`} />
            {verifyPending ? 'Checking...' : 'Check connection'}
          </Button>
        </div>
      </section>

      <div className="ml-0 sm:ml-12 mt-12 pt-8 border-t border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4">Advanced settings</h3>
        <div className="bg-card border border-card-border rounded-xl p-6 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div>
            <h4 className="font-medium text-foreground mb-1">Security Token</h4>
            <p className="text-sm text-muted-foreground max-w-lg">
              If your embed token is compromised, you can rotate it here. 
              You will need to update the widget script on your website afterward.
            </p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" data-testid="button-rotate-token" className="shrink-0">
                <KeyRound className="w-4 h-4 mr-2" />
                Rotate Token
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Rotate security token?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will immediately invalidate your current embed snippet. The widget will stop working on your site until you replace the old script with the newly generated one.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={onRotate}
                  disabled={rotatePending}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {rotatePending ? 'Rotating...' : 'Rotate Token'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}
